import { Logger } from 'fsts';
const logger = new Logger('generalUtils');

// From https://github.com/mattphillips/deep-object-diff
export const guidEmpty = '00000000-0000-0000-0000-000000000000';
export const isDate = (d: any) => d instanceof Date;
export const isEmpty = (o: any) => Object.keys(o).length === 0;
export const isObject = (o: any) => o != null && typeof o === 'object';
export const properObject = (o: any) =>
  isObject(o) && !o.hasOwnProperty ? { ...o } : o;

// Returns only the values that have been changed in the updated object
const updatedDiff = (lhs: any, rhs: any) => {
  if (lhs === rhs) return {};

  if (!isObject(lhs) || !isObject(rhs)) return rhs;

  const l = properObject(lhs);
  const r = properObject(rhs);

  if (isDate(l) || isDate(r)) {
    if (l.valueOf() == r.valueOf()) return {};
    return r;
  }

  return Object.keys(r).reduce((acc, key) => {
    // eslint-disable-next-line no-prototype-builtins
    if (l.hasOwnProperty(key)) {
      const difference: any = updatedDiff(l[key], r[key]);

      if (isObject(difference) && isEmpty(difference) && !isDate(difference))
        return acc;

      return { ...acc, [key]: difference };
    }

    return acc;
  }, {});
};

export default class GeneralUtils {
  public static isDevEnvironment(): boolean {
    return process.env.NODE_ENV == 'development' || window.location.host.includes('localhost');
  }
  public static isProdEnvironment(): boolean {
    return process.env.NODE_ENV == 'production';
  }
  public static camelToSnakeCase(str: string) {
    let result = str
      .replace(/^[A-Z]/g, letter => `${letter.toLowerCase()}`)
      .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    logger.debug(`camelToSnake:${str}->${result}`);
    return result;
  }
  public static maxint = 2147483647;
  public static updatedDiff = (lhs: any, rhs: any) => {
    return updatedDiff(lhs, rhs);
  };

  /**
   * Creates a deep copy of an object or array.
   * Useful for initializing form data from a Vuex store or Prop.
   */
  public static cloneDeep<T>(val: T): T {
    if (val === null || typeof val !== 'object') {
      return val;
    }

    if (val instanceof Date) {
      return new Date(val.getTime()) as any;
    }

    if (Array.isArray(val)) {
      return val.map((item) => this.cloneDeep(item)) as any;
    }

    const clonedObj: any = {};
    for (const key in val) {
      if (Object.prototype.hasOwnProperty.call(val, key)) {
        clonedObj[key] = this.cloneDeep(val[key]);
      }
    }
    return clonedObj;
  }

  /**
   * Deeply compares two values.
   * Useful for checking if the form has changed before saving.
   */
  public static isEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== typeof b) return false;

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (typeof a === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key) || !this.isEqual(a[key], b[key])) {
          return false;
        }
      }
      return true;
    }

    return false;
  }


  /**
 * Creates a debounced function that delays invoking `func` until after `wait` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * 
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns The debounced function with a `cancel` method
 */
public static debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 500
): T & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = function (this: any, ...args: Parameters<T>) {
    const context = this;
    
    // Clear previous timeout
    if (timeout) {
      clearTimeout(timeout);
    }
    
    // Set new timeout
    timeout = setTimeout(() => {
      func.apply(context, args);
      timeout = null;
    }, wait);
  } as T & { cancel: () => void };

  // Add cancel method
  debounced.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

  // clear field `@odata.type` from object ( example `{ "@odata.type": "#piex.Features.ArticleMarker.ArticleMarkerDto", id: 5, name: "AAC1220" },`) 
  public static removeODataType(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(GeneralUtils.removeODataType);
    } else if (obj !== null && typeof obj === "object") {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== "@odata.type") {
          newObj[key] = GeneralUtils.removeODataType(obj[key]);
        }
      }
      return newObj;
    } else {
      return obj;
    }
  }


}
